import * as Validator from "yup"
import { IHotel } from "./store"
import { Dialog } from "@sembark-travel/ui-dialog"
import { Alert, Button, Inline, Stack } from "@sembark-travel/ui-base"
import {
	Form,
	GetFieldValue,
	SelectField,
	SubmissionError,
	TextAreaInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { SelectMealPlans } from "../MealPlans"
import { showSnackbar } from "@sembark-travel/ui-snackbar"

interface IQuickAddMealPlanProps {
	open: boolean
	hotel: IHotel
	onCancel: () => void
	onSuccess?: (hotel: IHotel, name?: string) => void
	name?: string
}

const quickAddMealPlanSchema = Validator.object({
	name: Validator.object({
		name: Validator.string().trim(),
	})
		.required("Please select or enter a name")
		.test(
			"has-name",
			"Please select or enter a name",
			(value) => !!value?.name?.trim()
		),
	description: Validator.string().nullable(),
})

type QuickAddMealPlanFormValues = Validator.InferType<
	typeof quickAddMealPlanSchema
>

export function AddMealPlanToHotel({
	open,
	hotel,
	onCancel,
	onSuccess,
	name,
}: IQuickAddMealPlanProps) {
	const xhr = useXHR()

	const initialValues: QuickAddMealPlanFormValues = {
		name: name ? { name } : {},
		description: "",
	}

	return (
		<Dialog open={open} onClose={onCancel} md>
			<Dialog.Header closeButton>
				<Dialog.Title>Quick Add Meal Plan</Dialog.Title>
			</Dialog.Header>

			<Form<QuickAddMealPlanFormValues>
				initialValues={initialValues}
				validate={validateFormValues(quickAddMealPlanSchema)}
				onSubmit={withServerErrors(async (values) => {
					if (!values.name?.name?.trim()) {
						throw new Error("Please select or enter a meal plan")
					}

					const response = await xhr.patch(`/hotels/${hotel.id}/meal-plans`, {
						name: values.name.name,
						description: values.description,
					})

					if (response.data.data) {
						onSuccess?.(response.data.data, values.name.name)
					}

					showSnackbar("Meal Plan added successfully!")
					onCancel()
				})}
				subscription={{
					submitting: true,
				}}
			>
				{({ submitting, handleSubmit }) => {
					return (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body paddingX="0">
								<Stack gap="4">
									<Stack paddingX="6" gap="4">
										<GetFieldValue<{
											id?: string
											name?: string
										}> name="name">
											{({ value }) => (
												<SelectField
													select={SelectMealPlans}
													name="name"
													creatable
													label="Meal Plan"
													fetchOnMount
													fullWidth
													filterOptions={(arr) => {
														// Exclude the initially selected name passed from the parent to prevent it from appearing twice in the options.
														if (!arr?.length) return []

														if (!value?.id && value?.name) {
															return arr.filter(
																(option) => option.name !== value.name
															)
														}

														return arr
													}}
												/>
											)}
										</GetFieldValue>

										<TextAreaInputField
											label="Description"
											name="description"
											placeholder="Dinner + Breakfast"
											type="text"
										/>

										<Alert>
											The description will be added for new Meal Plans and
											ignored for existing ones.
										</Alert>
										<SubmissionError />
									</Stack>
								</Stack>
							</Dialog.Body>

							<Dialog.Footer>
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save Meal Plan"}
									</Button>

									<Button
										type="button"
										onClick={onCancel}
										disabled={submitting}
									>
										Cancel
									</Button>
								</Inline>
							</Dialog.Footer>
						</form>
					)
				}}
			</Form>
		</Dialog>
	)
}
